import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { lazy, Suspense, useEffect } from 'react';

import Layout from './components/Layout';
import RootRedirect from './components/RootRedirect';
import Toast from './components/Toast';
import PushNotificationManager from './components/PushNotificationManager';
import UpdateNotification from './components/UpdateNotification';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SuperAdminRoute from './components/SuperAdminRoute';
import ProtectedRoute from './components/ProtectedRoute'; // Import added
import { useServiceWorker } from './hooks/useServiceWorker';
import PageLoader from './components/PageLoader';
import DemoController from './components/DemoController'; 

// Mobile Capacitor Imports
import { StatusBar } from '@capacitor/status-bar';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';

// Helper para imports dinámicos resistentes a fallas de red y actualizaciones en producción (óptimo para redes lentas)
function safeLazy(importFunc: () => Promise<any>) {
  return lazy(() => 
    importFunc().catch((error) => {
      console.warn("Dynamic import failed, retrying or refreshing...", error);
      // Detectamos si es un error de carga de chunk (típico en despliegues o pérdidas de conexión)
      const isChunkLoadFailed = error.name === 'ChunkLoadError' || 
        /Failed to fetch dynamically imported module|Loading chunk|ChunkLoadError/i.test(error.message);
      
      if (isChunkLoadFailed) {
        const lastReload = sessionStorage.getItem('last_chunk_reload');
        const now = Date.now();
        
        // Si no ha habido un refresco en los últimos 10 segundos, refrescar
        if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
          sessionStorage.setItem('last_chunk_reload', now.toString());
          window.location.reload();
          return new Promise(() => {}); // Promesa pendiente para suspender el renderizado limpiamente
        }
      }
      throw error;
    })
  );
}

// Lazy loading de componentes de análisis para no bloquear el hilo principal
const SpeedInsights = safeLazy(() => import('@vercel/speed-insights/react').then(m => ({ default: m.SpeedInsights })));
const Analytics = safeLazy(() => import('@vercel/analytics/react').then(m => ({ default: m.Analytics })));

// Lazy loading de páginas para mejorar rendimiento inicial
const Catalog = safeLazy(() => import('./pages/Catalog'));
const ProductForm = safeLazy(() => import('./pages/ProductForm'));
const Categories = safeLazy(() => import('./pages/Categories'));
const Coupons = safeLazy(() => import('./pages/Coupons'));
const Inventory = safeLazy(() => import('./pages/Inventory'));
const InventoryMovement = safeLazy(() => import('./pages/InventoryMovement'));
const Orders = safeLazy(() => import('./pages/Orders'));
const BusinessProfile = safeLazy(() => import('./pages/BusinessProfile'));
const StorePage = safeLazy(() => import('./pages/StorePage'));
const Login = safeLazy(() => import('./pages/Login'));
const StoreSettings = safeLazy(() => import('./pages/StoreSettings'));
const NotFound = safeLazy(() => import('./pages/NotFound'));
const AccountInfo = safeLazy(() => import('./pages/AccountInfo'));
const ProductAddons = safeLazy(() => import('./pages/ProductAddons'));
const OrderStatus = safeLazy(() => import('./pages/OrderStatus'));
const Dashboard = safeLazy(() => import('./pages/Dashboard'));
const CreateOrder = safeLazy(() => import('./pages/CreateOrder'));
const ProductDetail = safeLazy(() => import('./pages/ProductDetail'));
const BusinessInfo = safeLazy(() => import('./pages/BusinessInfo'));
const Cart = safeLazy(() => import('./pages/Cart'));
const Checkout = safeLazy(() => import('./pages/Checkout'));
const PricingPage = safeLazy(() => import('./pages/PricingPage'));
const MarketingPage = safeLazy(() => import('./pages/MarketingPage'));
const SearchPage = safeLazy(() => import('./pages/SearchPage'));
const Marketplace = safeLazy(() => import('./pages/Marketplace'));
// const MarketplaceMapPage = safeLazy(() => import('./pages/MarketplaceMapPage')); // Deleted
// const MarketplaceMapPage = safeLazy(() => import('./pages/MarketplaceMapPageTest')); // TEST VERSION
const SuperAdminPage = safeLazy(() => import('./pages/SuperAdminPage'));
const SuperAdminUserPage = safeLazy(() => import('./pages/SuperAdminUserPage'));
const MenuPage = safeLazy(() => import('./pages/MenuPage'));
const MarginCalculator = safeLazy(() => import('./pages/tools/MarginCalculator'));
const CostCalculator = safeLazy(() => import('./pages/CostCalculator'));

// POS Components (Palladium)
const POSLayout = safeLazy(() => import('./pages/pos/POSLayout'));
const Terminal = safeLazy(() => import('./pages/pos/Terminal'));
const Tables = safeLazy(() => import('./pages/pos/Tables'));
const CashPage = safeLazy(() => import('./pages/pos/CashPage'));
const MobileScanner = safeLazy(() => import('./pages/pos/MobileScanner'));
const ProductLabels = safeLazy(() => import('./pages/pos/ProductLabels'));
const TeamManagement = safeLazy(() => import('./pages/TeamManagement'));
const PinPad = safeLazy(() => import('./pages/pos/PinPad'));
const QRGenerator = safeLazy(() => import('./pages/tools/QRGenerator'));
// const RecuperarContrasena = safeLazy(() => import('./pages/RecuperarContrasena'));

function App() {

  // Sistema de actualización automática para producción
  const { isUpdateAvailable, applyUpdate } = useServiceWorker();
  const location = useLocation();

  // Detección de Median App para estilos globales
  useEffect(() => {
    const checkMedian = () => {
      // Importamos dinámicamente o usamos la función de lib
      import('./lib/median').then(({ isMedianApp }) => {
        if (isMedianApp()) {
          document.body.classList.add('median-app');
        }
      });
    };
    checkMedian();
  }, []);

  // Full Screen Mode for Mobile App (Android/Capacitor)
  useEffect(() => {
    const initMobile = async () => {
      try {
        // Ocultar barra de estado (superior)
        await StatusBar.hide();
        // Ocultar barra de navegación (inferior) - Modo Inmersivo
        await NavigationBar.hide();

        // Re-ocultar si el usuario interactúa o el sistema las muestra (Sticky)
        window.addEventListener('focus', () => {
          StatusBar.hide();
          NavigationBar.hide();
        });
      } catch (e) {
        // Silencioso si no estamos en entorno nativo (web)
      }
    };
    initMobile();
  }, []);

  // Rutas donde NO se debe mostrar el chatbot (rutas públicas - tienda del cliente)
  // const publicRoutes = [
  //   '/login',
  //   '/store/',
  //   '/tienda/',
  //   '/menu/',
  //   '/cart',
  //   '/carrito',
  //   '/checkout',
  //   '/order-status',
  //   '/pricing',
  //   '/store-not-found',
  //   '/marketplace',
  //   '/marketplace/mapa'
  // ];
  // ];

  // Verificar si la ruta actual es pública
  // const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  return (
    <HelmetProvider>
      <Toast />
      <PushNotificationManager />
      <DemoController />

      {/* Notificación de actualización disponible */}
      <UpdateNotification
        isVisible={isUpdateAvailable}
        onUpdate={applyUpdate}
      />

      {/* Prompt para instalar la aplicación PWA */}
      <PWAInstallPrompt />

      {/* Speed Insights & Analytics - Lazy loaded */}
      <Suspense fallback={null}>
        <SpeedInsights />
        <Analytics />
      </Suspense>

      <AnimatePresence mode="wait">
        <Suspense fallback={<PageLoader />}>
          <Routes location={location} key={location.pathname}>
            {/* Home Page - Landing page pública o redirección si está autenticado */}
            <Route path="/" element={<RootRedirect />} />

            {/* Recuperación de Contraseña - Temporalmente comentado por error de build */}
            {/* <Route path="/recuperar-contrasena" element={<RecuperarContrasena />} /> */}

            {/* Admin Dashboard Routes */}
            <Route path="/" element={<Layout />}>
              <Route path="login" element={<Login />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<BusinessProfile />} />
              <Route path="settings" element={<StoreSettings />} />
              <Route path="account" element={<AccountInfo />} />
              <Route element={<ProtectedRoute requiredPermission="allow_marketing" />}>
                <Route path="marketing" element={<MarketingPage />} />
              </Route>
              <Route path="calculator" element={<CostCalculator />} />

              {/* Módulos protegidos por permisos */}
              <Route element={<ProtectedRoute requiredPermission="catalog" />}>
                <Route path="catalog" element={<Catalog />} />
                <Route path="catalog/labels" element={<ProductLabels />} />
                <Route path="product/new" element={<ProductForm />} />
                <Route path="product/:id" element={<ProductForm />} />
                <Route path="product-addons" element={<ProductAddons />} />
                <Route path="categories" element={<Categories />} />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="coupons" />}>
                <Route path="coupons" element={<Coupons />} />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="inventory" />}>
                <Route path="inventory" element={<Inventory />} />
                <Route path="inventory/movement" element={<InventoryMovement />} />
                <Route path="inventory/movement/:productId" element={<InventoryMovement />} />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="orders" />}>
                <Route path="orders" element={<Orders />} />
                <Route path="orders/new" element={<CreateOrder />} />
              </Route>

              {/* Gestión de Equipo */}
              <Route path="team" element={<TeamManagement />} />
            </Route>
            <Route path="store/:businessId" element={<StorePage />} />
            <Route path="tienda/:storeSlug" element={<StorePage />} />
            <Route path="tienda/:storeSlug/info" element={<BusinessInfo />} />
            <Route path="tienda/:storeSlug/carrito" element={<Cart />} />

            <Route path="tienda/:storeSlug/checkout" element={<Checkout />} />
            <Route path="tienda/:storeSlug/buscar" element={<SearchPage />} />
            <Route path="tienda/:storeSlug/:productSlug" element={<ProductDetail />} />

            <Route path="pricing" element={<PricingPage />} />
            <Route path="order-status/:orderId" element={<OrderStatus />} />
            <Route path="order-status" element={<OrderStatus />} />
            <Route path="store-not-found" element={<NotFound />} />

            {/* Herramientas */}
            <Route path="herramientas/qr" element={<QRGenerator />} />
            <Route path="herramientas/margen" element={<MarginCalculator />} />

            {/* Subdomain / Simplified Routes */}
            <Route path="cart" element={<Cart />} />
            <Route path="carrito" element={<Cart />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="buscar" element={<SearchPage />} />
            <Route path="info" element={<BusinessInfo />} />
            {/* Note: :productSlug at root level must be placed carefully to avoid collisions, 
                but since we have specific routes above, React Router will match those first. 
                We place this late to act as a catch-all for products on subdomains. */}
            <Route path=":productSlug" element={<ProductDetail />} />

            {/* Marketplace Route - Future Home */}
            <Route path="marketplace" element={<Marketplace />} />
            {/* <Route path="marketplace/mapa" element={<MarketplaceMapPage />} />  Deleted */}

            {/* Menu Route - Restaurant Style */}
            <Route path="menu/:storeSlug" element={<MenuPage />} />
            <Route path="menu/:storeSlug/:productSlug" element={<ProductDetail />} />

            {/* Super Admin Route - Hiding checks done inside component */}
            <Route
              path="/sa-control-panel"
              element={
                <SuperAdminRoute>
                  <SuperAdminPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="/sa-control-panel/user/:userId/permissions"
              element={
                <SuperAdminRoute>
                  <SuperAdminUserPage />
                </SuperAdminRoute>
              }
            />

            {/* POS Routes (Palladium Only) */}
            <Route path="/pos" element={<POSLayout />}>
              {/* Changed index from terminal to pinpad */}
              <Route index element={<Navigate to="pinpad" replace />} />
              <Route path="pinpad" element={<PinPad />} />
              <Route path="terminal" element={<Terminal />} />
              <Route path="tables" element={<Tables />} />
              <Route path="cash" element={<CashPage />} />
              <Route path="labels" element={<ProductLabels />} />
              <Route path="scanner" element={<MobileScanner />} />
              {/* Future routes: kitchen */}
            </Route>

            {/* Mobile/Direct Scanner Route - Keep for easy mobile access */}
            <Route path="scan" element={<MobileScanner />} />
            <Route path="scanner" element={<MobileScanner />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AnimatePresence>
    </HelmetProvider>
  );
}

export default App;
// Trigger redeploy to clear map changes